import { Fragment } from 'react';
import { createIntl, FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import {
  ROUTE_SESSIONS_ENTITY_SESSION,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSONS,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA_EDIT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE,
  ROUTE_SESSIONS_ENTITY_SESSION_EDIT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA
} from 'routes/RouteList';

import { TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import Badge from 'components/badge/Badge';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';
import { useSessionContext } from 'contexts/SessionContext';
import SessionForm from 'containers/pages/prevention/SessionForm';
import Loader from 'components/loader/Loader';
import { TestsProvider } from 'contexts/TestsContext';
import messages from 'messages';
import useTabIndex from 'hooks/utils/useTabIndex';
import TabLink from 'components/tabs/TabLink';
import useEdit from 'hooks/utils/useEdit';
import SessionHeader from 'containers/pages/sessions/SessionHeader';
import useSelectable from 'hooks/utils/useSelectable';
import SessionDetailTab from 'containers/pages/sessions/SessionDetailTab';
import { getLocalDateFormat } from 'utils/date';
import SessionPersonsTab from 'containers/pages/sessions/SessionPersonsTab';
import slugify from 'slugify';
import { Panel, PanelBody } from 'components/panel';

const Session = () => {
  const intl = useIntl();
  const enIntl = createIntl({ locale: 'en' });
  const { session, sessionLoading, error } = useSessionContext();
  const { selected, setSelected } = useSelectable();
  const { entityId, sessionsTab, sessionId } = useParams();
  const [edit, setEdit] = useEdit(
    !!useRouteMatch([ROUTE_SESSIONS_ENTITY_SESSION_EDIT]),
    true
  );
  const [tabIndex, setTabIndex] = useTabIndex([
    [ROUTE_SESSIONS_ENTITY_SESSION, ROUTE_SESSIONS_ENTITY_SESSION_EDIT],
    [
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSONS,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA_EDIT,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE
    ]
  ]);

  const active = useRouteMatch([
    ROUTE_SESSIONS_ENTITY_SESSION_PERSONS,
    ROUTE_SESSIONS_ENTITY_SESSION
  ]);

  if (sessionLoading) {
    return (
      <Panel active={active.isExact}>
        <Loader />
      </Panel>
    );
  }

  return (
    <Fragment>
      <Panel active={active.isExact}>
        {error && <RetryPanel />}
        {session && (
          <div style={{ background: 'inherit' }}>
            <SessionHeader
              tabIndex={tabIndex}
              selectedSporters={selected}
              title={session.name}
              isExpired={session.isRecurring && session.enddate < new Date()}
              subtitle={
                session.isRecurring
                  ? intl.formatMessage(messages.recurringSessionSubtitle, {
                      type: session.interval,
                      startdate: getLocalDateFormat(session.startdate),
                      enddate: getLocalDateFormat(session.enddate)
                    })
                  : getLocalDateFormat(session.startdate)
              }
              onClearSelected={() => setSelected([])}
            />
            <PanelBody>
              <Tabs
                selectedIndex={tabIndex}
                onSelect={index => setTabIndex(index)}
              >
                <TabList>
                  <TabLink
                    dataQa={`session-${slugify(enIntl.formatMessage(messages.tabSessionDetail).toLowerCase())}`}
                    to={generatePath(ROUTE_SESSIONS_ENTITY_SESSION, {
                      sessionId,
                      sessionsTab,
                      entityId
                    })}
                  >
                    <FormattedMessage {...messages.tabSessionDetail} />
                  </TabLink>
                  <TabLink
                    dataQa={`session-${slugify(enIntl.formatMessage(messages.tabSessionSporters).toLowerCase())}`}
                    to={generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSONS, {
                      sessionId,
                      sessionsTab,
                      entityId
                    })}
                  >
                    <Badge>{session.personEntities.length}</Badge>
                    <FormattedMessage {...messages.tabSessionSporters} />
                  </TabLink>
                </TabList>
                <TabPanel>
                  <TabPanelBody>
                    <TestsProvider entityId={entityId}>
                      <SessionForm
                        entityId={entityId}
                        session={session}
                        selected={selected}
                        onComplete={() => setEdit(false)}
                      >
                        <SessionDetailTab
                          edit={edit}
                          selectedPersons={selected}
                        />
                      </SessionForm>
                    </TestsProvider>
                  </TabPanelBody>
                </TabPanel>
                <TabPanel>
                  <TabPanelBody>
                    <SessionPersonsTab
                      entityId={entityId}
                      sessionId={sessionId}
                      onSelectPersons={persons => setSelected(persons)}
                    />
                  </TabPanelBody>
                </TabPanel>
              </Tabs>
            </PanelBody>
          </div>
        )}
      </Panel>
    </Fragment>
  );
};

export default Session;
